/**
 * Trust Service - Fetch trust scores from Governance API
 *
 * Uses the same endpoint as admin-ui for consistent trust scores:
 * GET /v1/governance/trust/{agentId}
 *
 * @raw-api apiClient returns backend JSON without case conversion.
 */

import { apiClient } from './api'

export interface TrustScore {
  agentId: string
  trustScore: number      // 0-100 scale (normalized)
  trustLevel: string      // 'low', 'moderate', 'high', 'exceptional'
  trustTier: number       // 0-3 (restricted, limited, standard, elevated)
  lastUpdated: string
}

interface GovernanceTrustResponse {
  agent_id?: string
  trust_score?: number
  trustScore?: number
  trust_level?: string
  trustLevel?: string
  trust_tier?: number
  trustTier?: number
  last_updated?: string
  lastUpdated?: string
}

class TrustService {
  /**
   * Get trust score for a specific agent from governance API
   * Returns null if trust data is not available
   */
  async getTrustScore(agentId: string): Promise<TrustScore | null> {
    try {
      const response = await apiClient.get<GovernanceTrustResponse>(
        `/v1/governance/trust/${encodeURIComponent(agentId)}`
      )

      // Handle both snake_case and camelCase responses
      const trustScore = response.trust_score ?? response.trustScore ?? 50
      const trustLevel = response.trust_level ?? response.trustLevel ?? 'moderate'
      const trustTier = response.trust_tier ?? response.trustTier ?? 2
      const lastUpdated = response.last_updated ?? response.lastUpdated ?? new Date().toISOString()

      // Trust score is already on 0-100 scale from API

      return {
        agentId,
        trustScore,
        trustLevel,
        trustTier,
        lastUpdated
      }
    } catch (error) {
      // Trust data not available for this agent
      console.debug(`No trust score available for agent ${agentId}:`, error)
      return null
    }
  }

  /**
   * Get trust scores for multiple agents
   * Returns a map of agentId -> TrustScore
   */
  async getTrustScoresForAgents(agentIds: string[]): Promise<Map<string, TrustScore>> {
    const trustMap = new Map<string, TrustScore>()

    // Fetch trust scores in parallel
    const promises = agentIds.map(async (agentId) => {
      const trustScore = await this.getTrustScore(agentId)
      if (trustScore) {
        trustMap.set(agentId, trustScore)
      }
    })

    await Promise.allSettled(promises)
    return trustMap
  }

  /**
   * Map trust level string to tier number (4-tier system)
   */
  mapTrustLevelToTier(trustLevel: string): number {
    const levelMap: Record<string, number> = {
      'unknown': 0,
      'critical': 0,
      'low': 0,
      'restricted': 0,
      'moderate': 1,
      'limited': 1,
      'high': 2,
      'standard': 2,
      'exceptional': 3,
      'elevated': 3
    }
    return levelMap[trustLevel.toLowerCase()] ?? 2
  }
}

export const trustService = new TrustService()
